import React from 'react';
import { Box, Typography, Checkbox, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import { Description, EmailOutlined, LanguageOutlined, LinkedIn, MessageOutlined, MoreHoriz, WhatsApp } from '@mui/icons-material';
import { ReactComponent as RoundDetailsIcon } from 'assets/rounds_icons/round_details_icon.svg';
import ImageAvatar from 'ui-components/ImageAvatar';
import { Draggable } from 'react-beautiful-dnd';
import { useTheme } from '@emotion/react';
import PitchReelButton from 'ui-components/PitchReelButton';
import { capitalizedSentence, formatDealSource } from '../../../utils';
import { CARD_SHADOW } from './KanbanColumn';

const BORDER = '#EAECF0';
const HEADING = '#101828';
const MUTED = '#667085';
// Backrr brand gradient (same as the Primary button).
const BRAND_GRADIENT = 'linear-gradient(135deg,#7852f8,#84aef6)';
// Solid (not rgba) so a dragged card never shows the board through it.
const SELECTED_BG = '#F8F6FF';

const isModifierClick = (e) => e.metaKey || e.ctrlKey || e.shiftKey;
const stop = (e) => e.stopPropagation();

// ClickUp-style outlined icon chip (24px). Clicks never start a drag or open the profile.
const ActionChip = ({ title, onClick, children, ...rest }) => (
    <Tooltip title={title}>
        <IconButton
            aria-label={title}
            onMouseDown={stop}
            onClick={(e) => { stop(e); onClick(e); }}
            sx={{
                width: 24, height: 24, p: 0, borderRadius: '6px',
                border: `1px solid ${BORDER}`, color: MUTED,
                '& svg': { fontSize: 14, width: 14, height: 14 },
                '&:hover': { backgroundColor: '#F4F3FF', borderColor: '#D9D6FE', color: '#444CE7' },
                '&:focus-visible': { outline: '2px solid #444CE7', outlineOffset: '1px' },
            }}
            {...rest}
        >
            {children}
        </IconButton>
    </Tooltip>
);

// Offset white "cards" behind a group drag (one layer for 2, two for 3+). In each pair the
// first shadow is the fill, the second the border.
// Cards stacked behind a group drag: tinted fill + solid edge, offset enough to read as "more than one".
const stackShadow = (count) => {
    const layers = ['10px 10px 0 -2px #F4F3FF', '10px 10px 0 0 #8E86EC'];
    if (count > 2) layers.push('20px 20px 0 -2px #F4F3FF', '20px 20px 0 0 #B3ADF2');
    return layers.join(', ');
};

// A draggable startup card. Clicking (not the checkbox/reel) opens the SAME profile route the table uses.
// Cmd/Ctrl/Shift+click selects instead. During a group drag the lifted card shows the
// group count; the other selected cards are hidden until the drop.
const KanbanCard = ({ card, index, reelStatuses, selected = false, onToggleSelect, onModifierSelect, dragCount = 0, onOpenReel, onOpenMenu, onCardAction }) => {
    const theme = useTheme();
    const indigo = theme?.palette?.customColors?.indigo600 || '#444CE7';
    const [messageAnchor, setMessageAnchor] = React.useState(null);
    const hasDeck = !!card?.pitchDeckFileUrl && !/undefined|null/.test(card.pitchDeckFileUrl);
    const act = (action) => () => onCardAction?.(action, card);
    // Same dark menu as the list row's Send Message.
    const messageItemSx = { padding: '0.625rem 1rem', gap: '8px', color: 'rgba(255,255,255,0.72)', '& svg': { fontSize: 18 }, '&:hover': { backgroundColor: theme?.palette?.customColors?.g500, color: '#fff' } };

    const handleCardClick = (e) => {
        if (!isModifierClick(e) || !onModifierSelect) return;
        e.preventDefault();
        if (e.shiftKey) window.getSelection()?.removeAllRanges(); // drop the text Shift+click highlights
        onModifierSelect(card, e);
    };

    const openProfile = (e) => {
        if (isModifierClick(e) && onModifierSelect) return; // handled by handleCardClick
        if (card?.startup_id) {
            window.open(`/deal-flow/view-profile?startup_id=${card.startup_id}`);
        }
    };

    // Stage leads the chip, the way the list's Stage column comes first in its row.
    const categorySector = [card?.stage, card?.category, card?.sector].filter(Boolean).join(' • ');

    return (
        <Draggable draggableId={String(card.pipeline_id)} index={index}>
            {(provided, snapshot) => (
                <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={handleCardClick}
                    sx={{
                        position: 'relative',
                        backgroundColor: selected ? SELECTED_BG : '#fff',
                        borderRadius: '10px',
                        padding: '0.95rem 1.1rem',
                        marginBottom: '0.5rem',
                        // ClickUp surface: no border; selected keeps an indigo ring.
                        boxShadow: snapshot.isDragging
                            ? (dragCount > 1 ? `${stackShadow(dragCount)}, 0 16px 32px rgba(16,24,40,0.16)` : '0 12px 28px rgba(16,24,40,0.14)')
                            : selected ? `0 0 0 1.5px #989DF2, ${CARD_SHADOW}` : CARD_SHADOW,
                        transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
                        '&:hover': { boxShadow: snapshot.isDragging ? undefined : selected ? `0 0 0 1.5px #989DF2, ${CARD_SHADOW}` : `0 0 0 1px #D5D9F5, ${CARD_SHADOW}` },
                        '&:hover .kb-card-menu, &:focus-within .kb-card-menu': { opacity: 1 },
                    }}
                >
                    {snapshot.isDragging && dragCount > 1 && (
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute', top: '-10px', right: '-10px',
                                height: 26, px: '0.65rem', borderRadius: '999px', whiteSpace: 'nowrap',
                                background: BRAND_GRADIENT, color: '#fff',
                                fontSize: '0.8rem', fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 10px rgba(120,82,248,0.35)',
                            }}
                        >
                            {dragCount} startups
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        {/* Checkbox — does not trigger drag or navigation. Width/visibility come from KanbanColumn (.kb-check). */}
                        <Box
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            className="kb-check"
                            sx={{ pt: '2px' }}
                        >
                            <Checkbox
                                size="small"
                                checked={selected}
                                onChange={() => onToggleSelect && onToggleSelect(card)}
                                sx={{
                                    p: 0,
                                    color: '#D0D5DD',
                                    '&.Mui-checked': { color: indigo },
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                }}
                            />
                        </Box>

                        {/* Logo + identity (click opens profile) */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={openProfile}>
                            {/* Same avatar as the table view: ImageAvatar derives the initials
                                and a name-hashed tint (utils/avatarColors), so a startup keeps
                                the same swatch in both views. The card's 36px/10px sizing is
                                kept, overriding the component's 2.5rem/9px default. */}
                            <ImageAvatar
                                src={card?.logo_url || ''}
                                alt={card?.startup_name}
                                width={36}
                                height={36}
                                style={{ width: 36, height: 36, borderRadius: '10px', fontSize: '0.82rem', fontWeight: 600 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                                    <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: HEADING, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {card?.startup_name || '-'}
                                    </Typography>
                                    {card?.source && (
                                        <Typography sx={{ fontSize: '0.75rem', color: '#475467', fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>{formatDealSource(card?.source)}</Typography>
                                    )}
                                </Box>
                                {/* Email and location lines always render (one line each) so every card is the same height. */}
                                <Typography sx={{ fontSize: '0.8rem', color: card?.email ? MUTED : '#98A2B3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: '3px' }}>
                                    {card?.email || 'Email not added'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: card?.location ? MUTED : '#98A2B3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: '2px' }}>
                                    {card?.location || 'Location not added'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Fixed row height = the reel button, so cards without a reel don't come out shorter. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '0.875rem', gap: '0.5rem', minHeight: 40 }}>
                        {categorySector ? (
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#737885', backgroundColor: '#f9f9fb', border: `1px solid ${BORDER}`, borderRadius: '6px', padding: '0.25rem 0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: `calc(100% - ${card?.video_pitch ? 3 : 0}rem)` }}>
                                {categorySector}
                            </Typography>
                        ) : <span />}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                            {card?.video_pitch && (
                                <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ flexShrink: 0, '& .MuiIconButton-root': { backgroundColor: '#f4f1fe' } }}>
                                    <PitchReelButton
                                        item={{ ...card, _id: card.startup_id }}
                                        statuses={reelStatuses}
                                        onOpenReel={onOpenReel}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Quick actions - the list row's icons, shown only when the startup has the data. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '0.625rem', minHeight: 24 }}>
                        {hasDeck && <ActionChip title="Pitch Deck" onClick={act('deck')}><Description /></ActionChip>}
                        {card?.website_url && <ActionChip title="Website" onClick={act('website')}><LanguageOutlined /></ActionChip>}
                        {card?.linkedin_url && <ActionChip title="LinkedIn" onClick={act('linkedin')}><LinkedIn /></ActionChip>}
                        {(card?.email || card?.phone) && (
                            <ActionChip
                                title="Send Message"
                                aria-haspopup="menu"
                                aria-expanded={messageAnchor ? 'true' : 'false'}
                                onClick={(e) => setMessageAnchor(e.currentTarget)}
                            >
                                <MessageOutlined />
                            </ActionChip>
                        )}
                        {card?.has_round && <ActionChip title="View Round Details" onClick={act('round')}><RoundDetailsIcon fill="currentColor" /></ActionChip>}
                        {/* Startup actions (same as the list row menu). Shown on card hover/focus only. */}
                        {onOpenMenu && (
                            <Tooltip title="More actions">
                                <IconButton
                                    className="kb-card-menu"
                                    aria-label={`More actions for ${card?.startup_name}`}
                                    onMouseDown={stop}
                                    onClick={(e) => { stop(e); onOpenMenu(card, e.currentTarget); }}
                                    sx={{ width: 24, height: 24, p: 0, ml: 'auto', borderRadius: '6px', opacity: 0, transition: 'opacity 0.15s ease', '&:focus-visible': { opacity: 1 }, '@media (hover: none)': { opacity: 1 } }}
                                >
                                    <MoreHoriz sx={{ fontSize: 20, color: '#475467' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    {messageAnchor && (
                        <Menu
                            anchorEl={messageAnchor}
                            open
                            onClose={() => setMessageAnchor(null)}
                            onClick={(e) => { stop(e); setMessageAnchor(null); }}
                            onMouseDown={stop}
                            onKeyDown={stop}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))', padding: '8px', borderRadius: '8px',
                                    '& .MuiList-root': { padding: '0 !important' },
                                    bgcolor: theme?.palette?.customColors?.g400, color: theme?.palette?.customColors?.whiteBG, minWidth: '170px',
                                },
                            }}
                        >
                            {card?.email && <MenuItem disableRipple sx={messageItemSx} onClick={act('email')}><EmailOutlined /> Email</MenuItem>}
                            {card?.phone && <MenuItem disableRipple sx={messageItemSx} onClick={act('whatsapp')}><WhatsApp /> WhatsApp</MenuItem>}
                        </Menu>
                    )}
                </Box>
            )}
        </Draggable>
    );
};

export default React.memo(KanbanCard);
